import { html } from '../lib/html.js';
import { maskPhone } from '../lib/masks.js';
import { openPhoto } from '../lib/ui.js';
import { fmtRating, hoursSummary, phoneLink, routeLink, showcase, timeOnApp, whatsappLink } from '../services/showcase.js';
import { Page, go } from './base.js';
import { replyTime } from '../services/reviews.js';
import { reportReview, reviewCard, summaryBlock } from './review-ui.js';
import { hsArrows } from '../lib/hscroll.js';
import { catTag } from './vehicle-hero.js';

const monthYear = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' });

/**
 * Perfil público da oficina (/oficinas/:id): galeria, selo, WhatsApp · Ligar · Como chegar,
 * números do app, especialidades, horário, endereço e descrição.
 * Os cliques contam no painel da oficina (a visita já conta ao abrir). Avaliações entram na Fase 3.
 * O responsável pode abrir o próprio perfil antes de ele entrar na vitrine (pré-visualização).
 */
class ShowcaseWorkshopPage extends Page {
  template() {
    return html`
      <ion-header translucent>
        <ion-toolbar>
          <ion-buttons slot="start"><ion-back-button default-href="/oficinas" text="Voltar"></ion-back-button></ion-buttons>
          <ion-title class="title">Empresa</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content class="surface-page" fullscreen>
        <div class="body"></div>
      </ion-content>`;
  }

  setup() {
    this.$('.body').addEventListener('click', (e) => {
      const t = e.target.closest('[data-track]');
      if (t && !this.own) showcase.track(this.wid, t.dataset.track); // o link abre normalmente
      const p = e.target.closest('[data-photo]');
      if (p) openPhoto(p.dataset.photo);
      const el = e.target.closest('[data-action]');
      if (el?.dataset.action === 'reload') this.load();
      if (el?.dataset.action === 'edit') go('/oficina/perfil');
      if (el?.dataset.action === 'all-reviews') go(`/oficinas/${this.wid}/avaliacoes`);
      if (el?.dataset.action === 'report') reportReview(el.dataset.id, el.dataset.reply === '1');
    });
    this.$('.body').addEventListener('scroll', (e) => {
      const g = e.target.closest?.('.gallery');
      if (g) this.updateCounter(g);
    }, true);
  }

  /** O ion-router passa :workshopId como propriedade; o pathname garante o primeiro carregamento. */
  get wid() {
    if (this.workshopId) return String(this.workshopId);
    return decodeURIComponent(location.pathname.match(/\/oficinas\/([^/?#]+)/)?.[1] ?? '');
  }

  onEnter() {
    this.load();
  }

  async load() {
    const body = this.$('.body');
    body.innerHTML = '<div class="center"><ion-spinner></ion-spinner></div>';
    try {
      const r = await showcase.get(this.wid);
      this.own = r.own;
      this.$('.title').textContent = r.workshop.name;
      body.innerHTML = profile(r.workshop, r).value;
    } catch (err) {
      body.innerHTML = html`
        <div class="center ion-padding">
          <ion-icon name="${err.status === 404 ? 'storefront-outline' : 'cloud-offline-outline'}" class="empty-icon"></ion-icon>
          <p>${err.status === 404 ? 'Esta empresa não está mais na vitrine.' : err.message}</p>
          ${err.status === 404 ? '' : html`<ion-button fill="outline" data-action="reload">Tentar de novo</ion-button>`}
        </div>`.value;
    }
  }

  updateCounter(g) {
    const i = Math.round(g.scrollLeft / g.clientWidth);
    const c = this.$('.gallery-count');
    if (c) c.textContent = `${i + 1} / ${g.children.length}`;
  }
}

function profile(w, { own, preview }) {
  const a = w.address;
  const since = timeOnApp(w.memberSince);
  const rv = w.reviews;
  const reply = replyTime(rv?.summary?.replyHours);
  const msg = `Olá! Encontrei a ${w.name} no TicketCar e gostaria de um orçamento.`;
  const actions = [
    w.whatsapp && html`<a class="act primary" href="${whatsappLink(w.whatsapp, msg)}" target="_blank" rel="noopener" data-track="whatsapp"><ion-icon name="logo-whatsapp"></ion-icon>WhatsApp</a>`,
    w.phone && html`<a class="act" href="${phoneLink(w.phone)}" data-track="call"><ion-icon name="call-outline"></ion-icon>Ligar</a>`,
    w.location && html`<a class="act" href="${routeLink(w.location)}" target="_blank" rel="noopener" data-track="route"><ion-icon name="navigate-outline"></ion-icon>Como chegar</a>`,
  ].filter(Boolean);

  return html`
    ${w.photos.length
      ? html`
        <div class="gallery-wrap hs-wrap">${w.photos.length > 1 ? hsArrows() : ''}
          <div class="gallery">${w.photos.map((p) => html`<button type="button" class="gallery-item" data-photo="${p.url}" aria-label="Ampliar foto"><img src="${p.url}" alt="Foto da empresa" /></button>`)}</div>
          ${w.photos.length > 1 ? html`<span class="gallery-count">1 / ${w.photos.length}</span>` : ''}
        </div>`
      : html`<div class="gallery-empty"><ion-icon name="storefront-outline"></ion-icon></div>`}

    <div class="narrow ion-padding shop-profile">
      ${preview
        ? html`
          <div class="notice warning">
            <ion-icon name="eye-outline"></ion-icon>
            <div><strong>Pré-visualização</strong><p>Só você vê este perfil agora. Ele entra na vitrine quando a empresa estiver aprovada e o perfil tiver endereço, mapa e telefone.</p></div>
          </div>`
        : ''}
      ${own ? html`<ion-button expand="block" fill="outline" data-action="edit"><ion-icon slot="start" name="create-outline"></ion-icon>Editar perfil público</ion-button>` : ''}

      <header class="shop-head">
        <h1>${w.name}</h1>
        <div class="badges">
          ${w.verified ? html`<span class="ver-badge"><ion-icon name="shield-checkmark-outline"></ion-icon>Empresa Verificada</span>` : ''}
          ${w.cnpjChecked ? html`<span class="muted-note">CNPJ conferido</span>` : ''}
        </div>
        <div class="shop-meta big">
          ${w.rating ? html`<b>${fmtRating(w.rating.avg)}</b><ion-icon class="star" name="star"></ion-icon><button type="button" class="link-btn" data-action="all-reviews">${w.rating.count} ${w.rating.count === 1 ? 'avaliação' : 'avaliações'}</button>` : html`<span class="new-tag">Nova no TicketCar</span>`}
          ${a?.district ? html`<span class="muted-note">· ${a.district}</span>` : ''}
        </div>
        <div class="cat-icons shop-head-lines">${(w.lines ?? ['LIGHT']).map((k) => catTag(k))}</div>
      </header>

      ${actions.length ? html`<div class="acts">${actions}</div>` : ''}

      <div class="panel shop-stats">
        <div><b>${w.servicesCount.toLocaleString('pt-BR')}</b><span>${w.servicesCount === 1 ? 'serviço registrado' : 'serviços registrados'} no app</span></div>
        <div><b>${since.value}</b><span>${since.label}</span></div>
        ${reply ? html`<div><b>${reply.value}</b><span>${reply.label}</span></div>` : ''}
      </div>

      ${w.specialties.length
        ? html`<h2 class="h2">Serviços</h2><div class="tags wrap">${w.specialties.map((s) => html`<span class="tag big">${s.name}</span>`)}</div>`
        : ''}

      <div class="panel info-panel">
        ${w.hours.length
          ? html`
            <div class="info-line">
              <ion-icon name="time-outline" class="${w.open?.open ? 'ok' : ''}"></ion-icon>
              <span>${w.open ? html`<b class="${w.open.open ? 'ok' : ''}">${w.open.open ? 'Aberta agora' : 'Fechada'}</b> · ` : ''}${hoursSummary(w.hours)}</span>
            </div>`
          : ''}
        ${a
          ? html`
            <div class="info-line">
              <ion-icon name="location-outline"></ion-icon>
              <span>${a.street}, ${a.number}${a.complement ? ` - ${a.complement}` : ''} · ${a.district}, ${a.city}/${a.state}</span>
            </div>`
          : ''}
        ${w.phone ? html`<div class="info-line"><ion-icon name="call-outline"></ion-icon><span>${maskPhone(w.phone)}</span></div>` : ''}
        ${w.description ? html`<p class="info-desc">${w.description}</p>` : ''}
      </div>

      ${rv
        ? html`
          <div class="rv-section-head">
            <h2 class="h2">Avaliações</h2>
            <span class="rv-note small"><ion-icon name="shield-checkmark-outline" aria-hidden="true"></ion-icon>Só de clientes com serviço registrado</span>
          </div>
          ${rv.summary.count
            ? html`${summaryBlock(rv.summary)}${rv.recent.map((r) => reviewCard(r, { reportable: !own }))}
              ${rv.summary.count > rv.recent.length ? html`<ion-button expand="block" fill="outline" data-action="all-reviews">Ver as ${rv.summary.count} avaliações</ion-button>` : ''}`
            : html`<div class="panel"><p class="panel-text">Ainda sem avaliações. Aqui só avalia quem teve um serviço registrado por esta empresa no TicketCar.</p></div>`}`
        : ''}

      ${w.verified
        ? html`
          <section class="verified-box">
            <span class="verified-icon"><ion-icon name="shield-checkmark-outline"></ion-icon></span>
            <div>
              <strong>Visitada pelo TicketCar</strong>
              <p>${w.verification?.visitedAt ? `Um representante esteve no local em ${monthYear.format(new Date(w.verification.visitedAt))}.` : 'Um representante esteve no local.'}</p>
              ${w.verification?.items?.length ? html`<ul class="verified-items">${w.verification.items.map((i) => html`<li><ion-icon name="checkmark-circle-outline" aria-hidden="true"></ion-icon>${i}</li>`)}</ul>` : ''}
            </div>
          </section>`
        : ''}

    </div>`;
}

customElements.define('page-showcase-workshop', ShowcaseWorkshopPage);
